import * as React from 'react';

export interface PointerEventProps {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  pointerClick?: (event: React.SyntheticEvent<any>) => void;
  pointerDown?: (event: React.SyntheticEvent<any>) => void;
  pointerDoubleClick?: (event: React.SyntheticEvent<any>) => void;
  pointerUp?: (event: React.SyntheticEvent<any>) => void;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  disabled?: boolean;
  children?: React.ReactElement;
}

/**
 * If overriding the default adapter, implement this interface. A pointer event adapter will follow
 * the lifecycle of the DOM react component it is attached to
 */
export interface PointerEventAdapter {

  /**
   * Given the synthetic events in the given PointerEventProps, this should return the React event
   * hooks needed to make it work.
   *
   * Called in every render.
   *
   * Must not return any non-event props.
   */
  getHtmlAttributes(props: PointerEventProps): React.HTMLAttributes<HTMLElement>;

  /**
   * Called in the DOM component's componentDidMount lifecycle hook
   */
  didMount?(domNode: Element, props: PointerEventProps): void;

  /**
   * Called in the DOM component's componentDidUpdate lifecycle hook
   */
  didUpdate?(domNode: Element, props: PointerEventProps): void;

  /**
   * Called in the DOM component's componentWillUnmount lifecycle hook
   */
  willUnmount?(): void;
}

/**
 * The default adapter does a direct mapping from pointer events to mouse event handlers.
 */
class DefaultPointerEventAdapter implements PointerEventAdapter {
  public getHtmlAttributes({ disabled, pointerClick, pointerDoubleClick, pointerDown, pointerUp }: PointerEventProps): React.HTMLAttributes<HTMLElement> {
    return {
      ...(pointerDoubleClick && { onDoubleClick: e => { if (!disabled) { pointerDoubleClick(e); } } }),
      ...(pointerDown && { onMouseDown: e => { if (!disabled) { pointerDown(e); } } }),
      ...(pointerUp && { onMouseUp: e => { if (!disabled) { pointerUp(e); } } }),
      ...(pointerClick && { onClick: e => { if (!disabled) { pointerClick(e); } } }),
    };
  }
}

export interface PointerEventAdapterFactory {
  (): PointerEventAdapter;
}

export class PointerEventAdapterManager {

  /**
   * Clients with custom pointer event systems should override the default factory
   */
  public static adapterFactory: PointerEventAdapterFactory = () => new DefaultPointerEventAdapter();
}
